import { useEffect, useState, useCallback } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { Loader } from "rsuite";

import PrescriberLayout from "../../components/PrescriberLayout";
import AlertBanner from "../../components/AlertBanner";
import { InputOtpCode } from "../../components/Inputs";
import API from "../../utils/api";
import { useConfig } from "../../context/config-context";


/**
 * OtpVerificationPage - Page publique d'authentification 2FA pour les prescripteurs
 *
 * Route: /prescriber-access/:permanentToken
 *
 * Fonctionnalités:
 * - Si session 2FA déjà active → redirection directe vers les documents
 * - Affichage du nom du prescripteur et du canal d'envoi
 * - Envoi automatique du code OTP à l'arrivée
 * - Renvoi de code avec délai de 60 secondes
 * - Validation du code OTP (max 3 tentatives)
 * - Gestion des erreurs selon les specs (code invalide, expiré, max tentatives)
 * - Heure d'expiration dynamique (mise à jour au renvoi)
 */
export default function OtpVerificationPage() {
  const { permanentToken } = useParams();
  const navigate = useNavigate();
  const siteConfig = useConfig();

  // Couleurs ecosystem
  const primaryColor = siteConfig.cssVariables?.["--primary"] || "#24335d";
  const accentColor = siteConfig.cssVariables?.["--dark"] || "#eb477d";

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  // Surveiller la valeur du champ pour activer/désactiver le bouton
  const codeValue = useWatch({ control, name: "code", defaultValue: "" });

  // États
  const [loading, setLoading] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [prescriberInfo, setPrescriberInfo] = useState(null);
  const [channel, setChannel] = useState(null); // "email" ou "sms"
  const [codeExpiresAt, setCodeExpiresAt] = useState(null); // Date d'expiration du code actuel
  const [alert, setAlert] = useState(null); // { type: "success"|"warning"|"error", message: "..." }
  const [countdown, setCountdown] = useState(0); // Délai avant renvoi (en secondes)
  const [isWaiting, setIsWaiting] = useState(false); // true pendant le countdown obligatoire
  const [showRetryButton, setShowRetryButton] = useState(false); // Bouton "Recevoir un nouveau code" inline

  /**
   * Récupère les informations du prescripteur et envoie le premier code OTP.
   * Si une session 2FA valide est déjà en sessionStorage, redirige directement vers /documents.
   */
  const fetchPrescriberInfo = useCallback(async () => {
    // Vérifier si une session active existe déjà
    const existingSession = sessionStorage.getItem("prescriberSessionToken");
    const storedToken = sessionStorage.getItem("permanentToken");

    if (existingSession && storedToken === permanentToken) {
      // Session potentiellement valide → redirection, documents-list-page vérifiera côté API
      navigate(`/prescriber-access/${permanentToken}/documents`, { replace: true });
      return;
    }

    try {
      setLoading(true);
      const response = await API.get(`/prescriber-access/${permanentToken}`, { silent: true });

      if (response.data.success) {
        const { prescriber, channel, expiresAt, wait, sendError } = response.data.data;
        setPrescriberInfo(prescriber);
        setChannel(channel);
        setCodeExpiresAt(expiresAt ? new Date(expiresAt) : null);

        if (wait?.code === "WAIT_60_SECONDS") {
          // Code déjà envoyé récemment — afficher le countdown sans bandeau success
          setIsWaiting(true);
          setAlert(null);
          setCountdown(wait.remainingSeconds);
        } else if (sendError) {
          // Échec d'envoi — afficher l'erreur avec bouton de renvoi
          setIsWaiting(false);
          setCountdown(0);
          setShowRetryButton(true);
          setAlert({
            type: "error",
            message: "L'envoi du code a échoué. Vous pouvez réessayer en cliquant sur le bouton.",
          });
        } else {
          setIsWaiting(false);
          setCountdown(60);
          // Pas de bandeau success à l'ouverture initiale de la page
        }
      }
    } catch (error) {
      setAlert({
        type: "error",
        message:
          error.response?.data?.error?.message ||
          "Erreur lors du chargement des informations.",
      });
    } finally {
      setLoading(false);
    }
  }, [permanentToken, navigate]);

  // Récupération des infos prescripteur et envoi du code initial
  useEffect(() => {
    fetchPrescriberInfo();
  }, [fetchPrescriberInfo]);

  // Gestion du compte à rebours pour le renvoi de code
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && isWaiting) {
      setIsWaiting(false);
    }
  }, [countdown, isWaiting]);

  // Auto-dismiss uniquement pour les bandeaux success (pas erreur/retry)
  useEffect(() => {
    if (!alert || alert.type !== "success") return;
    const timer = setTimeout(() => setAlert(null), 5000);
    return () => clearTimeout(timer);
  }, [alert]);

  /**
   * Renvoie un nouveau code OTP et met à jour l'heure d'expiration affichée
   */
  const handleResendCode = async () => {
    // Vérification côté client avant d'appeler l'API
    if (countdown > 0) {
      setIsWaiting(true);
      return;
    }

    try {
      setLoadingResend(true);
      setAlert(null);
      setShowRetryButton(false);

      const response = await API.post(
        `/prescriber-access/${permanentToken}/resend-code`,
        {},
        { silent: true }
      );

      if (response.data.success) {
        const { channel, expiresAt } = response.data.data;
        const channelLabel = channel === "email" ? "mail" : "SMS";

        setChannel(channel);
        // Mettre à jour l'heure d'expiration du nouveau code
        setCodeExpiresAt(expiresAt ? new Date(expiresAt) : null);
        setIsWaiting(false);
        setAlert({
          type: "success",
          message: `Un nouveau code de vérification vient de vous être envoyé par ${channelLabel}.`,
        });
        setCountdown(60);
      }
    } catch (error) {
      const errorData = error.response?.data?.error;

      if (errorData?.code === "WAIT_60_SECONDS") {
        setIsWaiting(true);
        setAlert(null);
        setCountdown(errorData.remainingSeconds || 60);
      } else {
        setAlert({
          type: "error",
          message: errorData?.message || "Erreur lors du renvoi du code.",
        });
      }
    } finally {
      setLoadingResend(false);
    }
  };

  /**
   * Bouton "Recevoir un nouveau code" dans les bandeaux d'erreur
   * Envoie un nouveau code et masque le bouton inline
   */
  const handleInlineResend = async () => {
    setShowRetryButton(false);
    await handleResendCode();
  };

  /**
   * Soumet le code OTP pour validation
   */
  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setAlert(null);
      setShowRetryButton(false);

      const response = await API.post(
        `/prescriber-access/${permanentToken}/verify-code`,
        { code: data.code },
        { silent: true }
      );

      if (response.data.success) {
        const { sessionToken } = response.data.data;

        // Stocker le token de session
        sessionStorage.setItem("prescriberSessionToken", sessionToken);
        sessionStorage.setItem("permanentToken", permanentToken);

        // Rediriger vers la liste des documents
        navigate(`/prescriber-access/${permanentToken}/documents`);
      }
    } catch (error) {
      const errorData = error.response?.data?.error;
      const code = errorData?.code;

      if (code === "CODE_INVALID") {
        const attempts = errorData?.attempts ?? 1;
        const remaining = 3 - attempts;
        setAlert({
          type: "error",
          message: `Le code saisi est incorrect, veuillez le vérifier. Votre code ne sera plus valide après ${remaining} saisie${remaining > 1 ? "s" : ""} en échec.`,
        });
      } else if (code === "CODE_EXPIRED") {
        setShowRetryButton(true);
        setAlert({
          type: "error",
          message: "Votre code n'est plus valide. Vous pouvez demander l'envoi d'un nouveau code en cliquant sur le bouton ci-contre.",
        });
      } else if (code === "CODE_MAX_ATTEMPTS" || code === "CODE_NOT_FOUND") {
        setShowRetryButton(true);
        setAlert({
          type: "error",
          message: "Votre code n'est plus valide. Vous pouvez demander l'envoi d'un nouveau code en cliquant sur le bouton ci-contre.",
        });
      } else {
        setAlert({
          type: "error",
          message: errorData?.message || "Erreur lors de la validation du code.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Loader initial
  if (!prescriberInfo && loading) {
    return (
      <PrescriberLayout>
        <div className="flex items-center justify-center py-10">
          <Loader size="lg" />
        </div>
      </PrescriberLayout>
    );
  }

  // Lien invalide ou expiré
  if (!prescriberInfo && !loading) {
    return (
      <PrescriberLayout>
        <div className="flex flex-col items-center gap-4 py-10">
          <p className="text-red-600">Lien invalide ou expiré</p>
        </div>
      </PrescriberLayout>
    );
  }

  const channelLabel = channel === "email" ? "mail" : "SMS";
  const maskedContact =
    channel === "email"
      ? prescriberInfo.maskedEmail
      : prescriberInfo.maskedPhone;

  // Heure d'expiration du code au format HHhMM
  const expiresAtLabel = codeExpiresAt
    ? codeExpiresAt.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      }).replace(":", "h")
    : null;

  // Bandeau hors card (warning countdown ou alerte)
  const bannerNode = (() => {
    if (isWaiting && countdown > 0) {
      return (
        <AlertBanner
          type="warning"
          message="Veuillez patienter 60 secondes avant de demander un nouveau code."
        />
      );
    }
    if (!isWaiting && alert) {
      return (
        <AlertBanner
          type={alert.type}
          message={
            showRetryButton ? (
              <span>
                {alert.message}{" "}
                <button
                  onClick={handleInlineResend}
                  disabled={loadingResend}
                  className="font-semibold underline hover:opacity-70 disabled:opacity-50"
                >
                  {loadingResend ? <Loader size="xs" /> : "Recevoir un nouveau code"}
                </button>
              </span>
            ) : (
              alert.message
            )
          }
        />
      );
    }
    return null;
  })();

  const isCodeComplete = (codeValue || "").length === 6;

  // Footer card : bouton Valider aligné à droite (desktop) / pleine largeur (mobile)
  const footerNode = (
    <div className="flex md:justify-end">
      <button
        type="submit"
        form="otp-form"
        disabled={loading || !isCodeComplete}
        className="w-full md:w-auto px-6 py-2 rounded font-semibold text-white transition-colors disabled:cursor-not-allowed"
        style={{
          backgroundColor: isCodeComplete && !loading ? primaryColor : "#9ca3af",
        }}
      >
        {loading ? <Loader size="xs" /> : "Valider"}
      </button>
    </div>
  );

  return (
    <PrescriberLayout banner={bannerNode} footer={footerNode}>
      {/* Formulaire */}
      <form
        id="otp-form"
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-5"
      >
        {/* En-tête prescripteur */}
        <div>
          <p className="text-base font-bold text-neutral-dark">Bonjour,</p>
          <p className="text-2xl font-bold" style={{ color: primaryColor }}>
            {prescriberInfo.civility} {prescriberInfo.lastName}
          </p>
        </div>

        {/* Message explicatif selon le canal */}
        <p className="text-sm text-neutral-dark">
          Afin de valider votre identité et sécuriser l'accès aux documents de
          votre file active, nous venons de vous envoyer un code par{" "}
          {channelLabel} {channel === "email" ? "à l'adresse " : "au "}
          <span className="">{maskedContact}</span>.
        </p>

        {/* Heure d'expiration + champ code */}
        <div className="flex flex-col gap-2 md:gap-4">
          {expiresAtLabel && (
            <p className="text-sm text-neutral-dark">
              Ce code est valide jusqu'à{" "}
              <span className="font-bold">{expiresAtLabel}</span>.
            </p>
          )}
          <div>
            <label className="block text-sm font-bold text-neutral-dark mb-2">
              Code d'accès * <span className="font-normal">(6 caractères)</span>
            </label>
            <InputOtpCode
              {...register("code", {
                required: "Le code est requis",
                minLength: {
                  value: 6,
                  message: "Le code doit contenir 6 caractères",
                },
                maxLength: {
                  value: 6,
                  message: "Le code doit contenir 6 caractères",
                },
              })}
              error={errors.code}
              placeholder="XXXXXX"
              maxLength={6}
              className="md:max-w-[330px]"
            />
          </div>
        </div>

        {/* Section "Vous n'avez rien reçu ?" */}
        <div className="overflow-hidden">
          {/* Titre fond accent — outline: 1px solid pour couvrir le border du parent */}
          <div
            className="relative px-4 py-2 rounded-t-[10px]"
            style={{
              backgroundColor: `${accentColor}80`,
              outline: `1px solid ${accentColor}80`,
            }}
          >
            <p className="text-sm font-bold text-neutral-dark">
              Vous n'avez rien reçu&nbsp;?
            </p>
          </div>
          {/* Corps fond blanc (mobile) / accent clair (desktop) */}
          <div className="rien-recu-body px-4 py-3 rounded-b-[10px] bg-white border border-neutral-lighter border-t-0">
            <ul
              className="text-sm list-disc list-inside space-y-1 mb-3"
              style={{ color: primaryColor }}
            >
              <li>Attendez 60 secondes</li>
              {channel === "email" ? (
                <li>Vérifiez vos messages indésirables</li>
              ) : (
                <li>Puis cliquez sur le lien ci-dessous</li>
              )}
            </ul>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={loadingResend}
              className="text-sm font-semibold underline hover:opacity-70 disabled:opacity-50 disabled:cursor-not-allowed text-left"
              style={{ color: primaryColor }}
            >
              {loadingResend ? (
                <Loader size="xs" />
              ) : (
                "Cliquez ici pour recevoir un nouveau code"
              )}
            </button>
          </div>
        </div>
      </form>
    </PrescriberLayout>
  );
}
